import { onlyDigits } from '@/components/CpfCnpjInput'
import { ApiClientError } from '@/lib/api/client'
import type { LandingDonor } from '@/lib/landing-auth'
import type { LandingRouletteHistoryItem } from '@/lib/landing-roulette'
import { displayNonePrizeHistoryLabel } from '@/lib/rouletteNoneDisplay'
import type { LandingCampaign } from '@/types/landing'

export type ProfileFormState = {
  person_type: 'pf' | 'pj'
  full_name: string
  corporate_name: string
  document: string
  email: string
  phone: string
  gender: string
  birth_date: string
  website: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  postal_code: string
}

export type PasswordFormState = {
  current_password: string
  new_password: string
  new_password_confirmation: string
}

const PROFILE_FIELD_TOTAL = 12

export function buildProfileFormState(donor: LandingDonor): ProfileFormState {
  return {
    person_type: donor.person_type === 'pj' ? 'pj' : 'pf',
    full_name: donor.full_name ?? donor.name ?? '',
    corporate_name: donor.corporate_name ?? '',
    document:
      donor.person_type === 'pj'
        ? (donor.document_cnpj ?? '')
        : (donor.document_cpf ?? ''),
    email: donor.email ?? '',
    phone: formatPhone(donor.phone ?? ''),
    gender:
      (
        { m: 'male', masculino: 'male', f: 'female', feminino: 'female' } as Record<
          string,
          string
        >
      )[donor.gender ?? ''] ??
      (['male', 'female', 'non_binary', 'prefer_not_to_say'].includes(donor.gender ?? '')
        ? donor.gender!
        : ''),
    birth_date: formatBirthDateInput(donor.birth_date ?? ''),
    website: donor.website ?? '',
    street: donor.address?.street ?? '',
    number: donor.address?.number ?? '',
    complement: donor.address?.complement ?? '',
    neighborhood: donor.address?.neighborhood ?? '',
    city: donor.address?.city ?? '',
    state: donor.address?.state ?? '',
    postal_code: formatPostalCode(donor.address?.postal_code ?? ''),
  }
}

export function createEmptyPasswordForm(): PasswordFormState {
  return { current_password: '', new_password: '', new_password_confirmation: '' }
}

export function formatPhone(value: string): string {
  const digits = onlyDigits(value).replace(/^55(?=\d{10,11}$)/, '').slice(0, 11)
  if (digits.length <= 2) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

export function formatPostalCode(value: string): string {
  const digits = onlyDigits(value).slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

export function formatBirthDateInput(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
  }
  const digits = onlyDigits(value).slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export function birthDateToIso(value: string): string | null {
  if (!value) return null
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (!match) return null
  const [, day, month, year] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  )
    return null
  return `${year}-${month}-${day}`
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

export function getDonorDisplayName(donor: LandingDonor | null): string {
  if (!donor) return 'Participante'
  if (donor.person_type === 'pj') {
    return donor.corporate_name || donor.name || donor.email || 'Participante'
  }
  return donor.full_name || donor.name || donor.email || 'Participante'
}

export function getInitials(donor: LandingDonor): string {
  const source =
    donor.person_type === 'pj'
      ? donor.corporate_name || donor.name || donor.email || 'U'
      : donor.full_name || donor.name || donor.email || 'U'

  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

export function countFilledFields(form: ProfileFormState): number {
  const values = [
    form.person_type,
    form.person_type === 'pj' ? form.corporate_name : form.full_name,
    form.document,
    form.email,
    form.phone,
    form.birth_date,
    form.street,
    form.number,
    form.neighborhood,
    form.city,
    form.state,
    form.postal_code,
  ]

  return values.filter((value) => String(value ?? '').trim() !== '').length
}

export function getProfileCompletionRate(form: ProfileFormState): number {
  return Math.round((countFilledFields(form) / PROFILE_FIELD_TOTAL) * 100)
}

export function flattenApiError(err: ApiClientError): string {
  const first = err.errors ? Object.values(err.errors).flat()[0] : null
  return first || err.message
}

export function isDonationOnlyCampaign(
  campaign: LandingCampaign | null | undefined
): boolean {
  return campaign?.generation_status === 'donation_only'
}

export function formatRouletteHistoryPrize(item: LandingRouletteHistoryItem): string {
  if (item.prize_type === 'pix') {
    return `PIX de R$ ${item.prize_payload?.amount?.toFixed(2)}`
  }
  if (item.prize_type === 'coupon') {
    return item.prize_payload?.discount_percent
      ? `Cupom de ${item.prize_payload.discount_percent}%`
      : `Cupom de R$ ${Number(item.prize_payload?.discount_amount ?? 0)
          .toFixed(2)
          .replace('.', ',')}`
  }
  if (item.prize_type === 'lucky_number') {
    return 'Número da Sorte Extra'
  }
  if (item.prize_type === 'none') {
    return displayNonePrizeHistoryLabel(item.prize_payload)
  }
  return 'Prêmio'
}
